import { HttpErrorResponse } from '@angular/common/http';
import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { catchError, EMPTY, switchMap, tap } from 'rxjs';

import { User } from '../../types/interfaces/auth/user';
import type { OrganizationRegistrationPayload } from '../../types/interfaces/auth/organization-registration-payload';
import { AuthStatus } from '../../types/types/auth-status';
import { Auth } from '../services/auth';
import { environment } from '../../../assets/environments/environment';
import { Session } from '../../types/interfaces/auth/session';
import { SnackbarService } from '../../shared/services/snackbar.service';
import { ORGANIZATION_STATUSES } from '../../types/types/organization-status';
import { USER_TYPES, UserType } from '../../types/types/user-type';

const initialState = {
  user: null as User | null,
  session: null as Session | null,
  token: null as string | null,
  loading: false,
  error: null as string | null,
  status: 'idle' as AuthStatus,
  isLoginMode: true,
};

export const AuthStore = signalStore(
  { providedIn: 'root' },
  withDevtools('AuthStore'),
  withState(initialState),
  withComputed((store) => ({
    isAuthenticated: () => store.user() !== null,
    isAdmin: () => store.user()?.role === 'admin',
    isOrgVerified: () => store.session()?.orgStatus === ORGANIZATION_STATUSES.VERIFIED,
    isUserIndividual: () => store.user()?.userType === USER_TYPES.INDIVIDUAL,
  })),
  withMethods((store) => {
    const authService = inject(Auth);
    const router = inject(Router);
    const snackbarService = inject(SnackbarService);

    async function fetchAndSetSession(): Promise<boolean> {
      const { data, error } = await authService.getSession();
      if (error) {
        patchState(store, {
          status: 'error',
          error: error.message ?? 'An error occurred while getting session',
          loading: false,
        });
        return false;
      }
      const user = (data?.user as unknown as User) ?? null;
      const sessionRaw = (data?.session as unknown as Session) || null;
      const session = sessionRaw
        ? { ...sessionRaw, organizationVerified: sessionRaw.organizationVerified ?? false }
        : null;
      patchState(store, {
        user,
        session,
        status: user ? 'success' : 'idle',
        loading: false,
      });
      return true;
    }

    return {
      setUser(user: User | null) {
        patchState(store, {
          user,
          error: null,
          status: user ? 'success' : 'idle',
        });
      },
      clearError() {
        patchState(store, {
          error: null,
          ...(store.status() === 'error' ? { status: 'idle' as AuthStatus } : {}),
        });
      },
      async signUpEmail(body: {
        email: string;
        password: string;
        image?: string;
        callbackURL?: string;
      }) {
        patchState(store, { loading: true });
        // TODO: callback url should come from the environment
        const { error } = await authService.signUpEmail({
          ...body,
          name: body.email,
          callbackURL: environment.frontendUrl + '/register-success',
        });
        if (error) {
          patchState(store, {
            loading: false,
            status: 'error',
            error: error.message ?? 'An error occurred while signing up',
          });
        } else {
          patchState(store, {
            loading: false,
            status: 'success',
          });
          router.navigate(['/email-confirmation'], {
            queryParams: { email: body.email },
          });
        }
      },
      async signInEmail(body: { email: string; password: string }) {
        patchState(store, { loading: true });
        const { error } = await authService.signInEmail(body);
        if (error) {
          patchState(store, {
            loading: false,
            status: 'error',
            error: error.message ?? 'An error occurred while signing in',
          });
        } else {
          await fetchAndSetSession();
          router.navigate(['/']);
        }
      },
      async signInGoogle() {
        patchState(store, { loading: true });
        const { error } = await authService.signInGoogle();
        if (error) {
          patchState(store, {
            error: error.message ?? 'An error occurred while signing in with Google',
            loading: false,
          });
        } else {
          patchState(store, { loading: false });
        }
      },
      async getSession() {
        patchState(store, { loading: true });
        await fetchAndSetSession();
      },
      async signOut() {
        const { error } = await authService.signOut();
        if (error) {
          patchState(store, {
            error: error.message ?? 'An error occurred while signing out',
            status: 'error',
          });
        } else {
          patchState(store, {
            user: null,
            token: null,
            session: null,
            error: null,
            status: 'idle',
          });
          snackbarService.showSuccess('You have been signed out');
          router.navigate(['/']);
        }
      },
      async updateUser(body: { name?: string; image?: string }) {
        patchState(store, { loading: true, error: null });
        const { error } = await authService.updateUser(body);
        if (error) {
          patchState(store, {
            loading: false,
            status: 'error',
            error: error.message ?? 'An error occurred while updating user',
          });
          return false;
        }
        await fetchAndSetSession();
        return true;
      },
      async changePassword(body: {
        currentPassword: string;
        newPassword: string;
        revokeOtherSessions?: boolean;
      }) {
        patchState(store, { loading: true, error: null });
        const { error } = await authService.changePassword(body);
        if (error) {
          patchState(store, {
            loading: false,
            status: 'error',
            error: error.message ?? 'An error occurred while changing password',
          });
          return false;
        }
        patchState(store, { loading: false, status: 'success' });
        return true;
      },
      async changeEmail(body: { newEmail: string; callbackURL?: string }) {
        patchState(store, { loading: true, error: null });
        const { error } = await authService.changeEmail(body);
        if (error) {
          patchState(store, {
            loading: false,
            status: 'error',
            error: error.message ?? 'An error occurred while changing email',
          });
          return false;
        }
        patchState(store, { loading: false, status: 'success' });
        return true;
      },
      setLoginMode(isLoginMode: boolean) {
        patchState(store, { isLoginMode });
      },
      registerOrganization: rxMethod<OrganizationRegistrationPayload>((trigger$) =>
        trigger$.pipe(
          tap(() => patchState(store, { loading: true, error: null })),
          switchMap((payload) =>
            authService.registerOrganization(payload).pipe(
              tap(() => {
                patchState(store, { loading: false, status: 'success' });
                router.navigate(['/email-confirmation'], {
                  queryParams: { email: payload.email, isIndividual: false, phone: payload.phone },
                });
              }),
              catchError((err: unknown) => {
                patchState(store, {
                  loading: false,
                  status: 'error',
                  error: mapOrganizationRegistrationHttpError(err),
                });
                return EMPTY;
              }),
            ),
          ),
        ),
      ),
    };
  }),
);

function mapOrganizationRegistrationHttpError(err: unknown): string {
  if (err instanceof HttpErrorResponse) {
    const body = err.error as { message?: string | string[] } | undefined;
    if (body?.message) {
      return Array.isArray(body.message) ? body.message.join(', ') : body.message;
    }
    if (err.status === 0) {
      return 'Network error. Please check your connection.';
    }
  }
  return 'Registration could not be completed. Please try again.';
}
